import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from 'reactstrap';
import AssignReviewerModal from './AssignReviewerModal';
import ScoreDetailsModal from './ScoreDetailsModal';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import { toast } from 'react-toastify';

const ApplicationRow = ({ application, visibleColumns, isSelected, onSelect, onUpdate }) => {
	const navigate = useNavigate();
	const [scoreModal, setScoreModal] = useState({ isOpen: false, round: null });
	const [resetting, setResetting] = useState(false);
	// Get user role on component mount

	const getStatusBadgeColor = (status) => {
		const statusColors = {
			Draft: 'warning',
			Submitted: 'info',
			'Review 1 - In Progress': 'primary',
			'Review 1 - Completed': 'success',
			'Review 2 - In Progress': 'primary',
			'Review 2 - Completed': 'success',
			Shortlisted: 'success',
			Rejected: 'danger',
		};
		return statusColors[status] || 'secondary';
	};

	const formatDate = (date) => {
		if (!date) return '-';
		return new Date(date).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	const openDocument = (url) => {
		if (!url) return;
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const titleCase = (s) => {
		if (!s && s !== 0) return '-';
		return String(s)
			.toLowerCase()
			.replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const handleResetReviews = async () => {
		if (!window.confirm(
			`Are you sure you want to reset all reviews for Application #${application.id}?\n\n` +
			'This will:\n' +
			'• Delete all scores and remarks\n' +
			'• Remove all reviewer assignments\n' +
			'• Reset status to "Submitted"\n\n' +
			'This action cannot be undone!'
		)) {
			return;
		}

		try {
			setResetting(true);
			const token = sessionStorage.getItem('token');

			const response = await axios.post(
				`${BASE_URL}/api/admin/dashboard/applications/${application.id}/reset-reviews`,
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data.success) {
				toast.success(
					`Application #${application.id} reviews reset successfully. ` +
					`Deleted: ${response.data.data.deleted.scores} scores, ` +
					`${response.data.data.deleted.remarks} remarks, ` +
					`${response.data.data.deleted.assignments} assignments.`
				);
				
				// Notify parent to refresh the list
				if (onUpdate) {
					onUpdate();
				}
			} else {
				toast.error(response.data.message || 'Failed to reset reviews');
			}
		} catch (error) {
			console.error('Error resetting reviews:', error);
			toast.error(error.response?.data?.message || 'Failed to reset application reviews');
		} finally {
			setResetting(false);
		}
	};

	const renderCell = (columnKey) => {
		switch (columnKey) {
			case 'id':
				return (
					<td>
						<strong>#{application.id}</strong>
					</td>
				);

			case 'select':
				return (
					<td className="text-center">
						<Input type="checkbox" checked={isSelected} onChange={() => onSelect(application.id)} />
					</td>
				);

			case 'name':
				return (
					<td>
						<div className="name-cell">
							<strong>{application.name}</strong>
						</div>
					</td>
				);

			case 'email':
				return (
					<td>
						<a href={`mailto:${application.email}`}>{application.email}</a>
					</td>
				);

			case 'phone':
				return <td>{application.phone || '-'}</td>;

			case 'institution':
				return <td>{application.institution || '-'}</td>;


			case 'degrees':
				return <td>{application.degree || '-'}</td>;

			case 'fieldOfStudy':
				return <td>{application.fieldOfStudy || '-'}</td>;

			case 'graduationYear':
				return <td>{application.graduationYear || '-'}</td>;

			case 'organisation':
				return <td>{application.organisation || '-'}</td>;

			case 'designation':
				return <td>{application.designation || '-'}</td>;

			case 'applicationDate':
				return <td>{formatDate(application.applicationDate)}</td>;

			case 'program':
				return <td>{application.program || '-'}</td>;

			case 'themes':
				return (
					<td>
						<div className="themes-cell">
							{Array.isArray(application.themes) && application.themes.length > 0 ? (
								application.themes.slice(0, 2).map((theme, idx) => (
									<Badge key={idx} color="secondary" className="me-1 mb-1">
										{theme}
									</Badge>
								))
							) : (
								'-'
							)}
							{application.themes?.length > 2 && (
								<Badge color="dark" className="mb-1">
									+{application.themes.length - 2}
								</Badge>
							)}
						</div>
					</td>
				);

			case 'gender':
				return <td>{application.gender || '-'}</td>;

			case 'state':
				return <td>{titleCase(application.state)}</td>;

			case 'country':
				return <td>{titleCase(application.country)}</td>;

			case 'round1Score':
				return (
					<td style={{ minWidth: '120px' }}>
						<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Round 1:</small>
						{application.round1Reviewer && (
							<div className="text-muted" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>{titleCase(application.round1Reviewer)}:</div>
						)}
						<Badge
							color={Number(application.round1Score) >= 70 ? 'success' : Number(application.round1Score) >= 50 ? 'warning' : 'danger'}
							style={{ cursor: application.round1Score ? 'pointer' : 'default' }}
							onClick={() => application.round1Score && setScoreModal({ isOpen: true, round: 'Round 1', reviewerId: null })}
						>
							{application.round1Score ? Number(application.round1Score).toFixed(2) : '-'}
						</Badge>
					</td>
				);

			case 'round2Score':
				// Parse Round 2 individual scores
				let round2Reviewers = [];
				if (application.round2IndividualScores) {
					const scores = application.round2IndividualScores.split('||');
					round2Reviewers = scores.map(score => {
						const [reviewerId, scoreValue, reviewerName] = score.split(':');
						return { reviewerId, score: parseFloat(scoreValue), reviewerName };
					});
				}

				const round2Completed = application.round2CompletedReviewers >= 2;

				return (
					<td style={{ minWidth: '140px' }}>
						<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
							Round 2:
							{!round2Completed && round2Reviewers.length > 0 && (
								<span className="text-warning ms-1" style={{ fontSize: '0.65rem' }}>
									({application.round2CompletedReviewers}/2 completed)
								</span>
							)}
						</small>
						{round2Reviewers.length > 0 ? (
							<>
								<div className="d-flex gap-2">
									{round2Reviewers.map((reviewer, idx) => (
										<div key={idx} style={{ fontSize: '0.75rem' }}>
											<div className="text-muted" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>{reviewer.reviewerName}:</div>
											<Badge
												color={reviewer.score >= 70 ? 'success' : reviewer.score >= 50 ? 'warning' : 'danger'}
												style={{ cursor: 'pointer' }}
												onClick={() => setScoreModal({ isOpen: true, round: 'Round 2', reviewerId: reviewer.reviewerId })}
											>
												{reviewer.score.toFixed(2)}
											</Badge>
										</div>
									))}
								</div>

							</>
						) : (
							<Badge color="secondary">-</Badge>
						)}
					</td>
				);

			case 'averageScore':
				return (
					<td style={{ minWidth: '100px' }}>
						<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Average:</small>
						{application.round2Score ? (
							<Badge
								color={Number(application.round2Score) >= 70 ? 'success' : Number(application.round2Score) >= 50 ? 'warning' : 'danger'}
							>
								{Number(application.round2Score).toFixed(2)}
							</Badge>
						) : (
							<Badge color="secondary">-</Badge>
						)}
					</td>
				);

			case 'status':
				return (
					<td>
						<Badge color={getStatusBadgeColor(application.status)}>{application.status}</Badge>
					</td>
				);

			case 'reviewStatus':
				return (
					<td>
						{application.reviewStatus ? (
							<Badge color={getStatusBadgeColor(application.reviewStatus)}>{application.reviewStatus}</Badge>
						) : (
							<span className="text-muted">-</span>
						)}
					</td>
				);

			case 'reviewer':
				return (
					<td>
						<div className="d-flex flex-column gap-1">
							{application.round1Reviewer ? (
								<div>
									<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Round 1:</small>
									<span style={{ fontSize: '0.85rem' }}>{application.round1Reviewer}</span>
								</div>
							) : (
								<div>
									<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Round 1:</small>
									<span className="text-muted" style={{ fontSize: '0.85rem' }}>Unassigned</span>
								</div>
							)}
							{application.round2Reviewers ? (
								<div>
									<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Round 2:</small>
									<span style={{ fontSize: '0.85rem' }}>{application.round2Reviewers}</span>
								</div>
							) : (
								<div>
									<small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Round 2:</small>
									<span className="text-muted" style={{ fontSize: '0.85rem' }}>Unassigned</span>
								</div>
							)}
						</div>
					</td>
				);

			case 'proposalTitle':
				return (
					<td>
						<div className="proposal-title" style={{ maxWidth: '200px' }}>
							{application.proposalTitle || '-'}
						</div>
					</td>
				);

			case 'files':
				return (
					<td>
						<div className="d-flex flex-wrap gap-1">
							{application.photoUrl && (
								<Badge color="info" className="me-1 mb-1 cursor-pointer"
									onClick={() => openDocument(application.photoUrl)}
									title="Photo"
									style={{ cursor: 'pointer' }}
								>Photo</Badge>
							)}
							{application.resumeUrl && (
								<Badge color="secondary" className="me-1 mb-1 cursor-pointer"
									onClick={() => openDocument(application.resumeUrl)}
									title="Resume"
									style={{ cursor: 'pointer' }}
								>Resume</Badge>
							)}
							{application.educationCertificateUrls && application.educationCertificateUrls.length > 0 && (
								application.educationCertificateUrls.map((certUrl, index) => (
									<Badge key={`cert-${index}`} color="success" className="me-1 mb-1"
										onClick={() => openDocument(certUrl)}
										title={`Certificate ${index + 1}`}
										style={{ cursor: 'pointer' }}
									>Certificate {index + 1}</Badge>
								))
							)}
							{application.bookProposalUrl && (
								<Badge
									color="warning"
									className="me-1 mb-1"
									onClick={() => openDocument(application.bookProposalUrl)}
									title="Book Proposal"
									style={{ cursor: 'pointer' }}
								>Book Proposal</Badge>
							)}
							{application.writingSampleUrl && (
								<Badge
									color="primary"
									className="me-1 mb-1"
									onClick={() => openDocument(application.writingSampleUrl)}
									title="Writing Sample"
									style={{ cursor: 'pointer' }}
								>Writing Sample</Badge>
							)}
							{application.recommendationLetters && application.recommendationLetters.length > 0 && (
								application.recommendationLetters.map((letterUrl, index) => (
									<Badge
										key={`rec-${index}`}
										color="info"
										className="me-1 mb-1"
										onClick={() => openDocument(letterUrl)}
										title={`Recommendation Letter ${index + 1}`}
										style={{ cursor: 'pointer' }}
									>
										Rec Letter {index + 1}
									</Badge>
								))
							)}
							{application.publicationFiles && application.publicationFiles.length > 0 && (
								application.publicationFiles.map((pub, index) => (
									pub.file_url && (
										<Badge
											key={`pub-${index}`}
											color="dark"
											className="me-1 mb-1"
											onClick={() => openDocument(pub.file_url)}
											title={pub.title || `Publication ${index + 1}`}
											style={{ cursor: 'pointer' }}
										>
											Pub {index + 1}
										</Badge>
									)
								))
							)}
							{application.additionalDocumentUrl && (
								<Badge
									color="light"
									className="me-1 mb-1"
									onClick={() => openDocument(application.additionalDocumentUrl)}
									title="Additional Document"
									style={{ cursor: 'pointer', color: '#000' }}
								>Additional Doc</Badge>
							)}
							{application.annexureUrl && (
								<Badge
									color="danger"
									className="me-1 mb-1"
									onClick={() => openDocument(application.annexureUrl)}
									title="Annexure"
									style={{ cursor: 'pointer' }}
								>Annexure</Badge>
							)}
						</div>
					</td>
				);

			case 'actions':
				return (
					<td>
						<div className="d-flex gap-2">
							<button
								className="btn btn-link p-0"
								onClick={() => navigate(`/admin/applications/${application.id}`)}
								title="View Full Application"
							>
								<i className="bi bi-eye-fill"></i>
							</button>
							{onUpdate && (
								<button
									className="btn btn-link p-0 text-danger"
									onClick={(e) => {
										e.stopPropagation();
										handleResetReviews();
									}}
									title="Reset all reviews and scores"
									disabled={resetting}
								>
									{resetting ? (
										<i className="bi bi-hourglass-split"></i>
									) : (
										<i className="bi bi-arrow-counterclockwise"></i>
									)}
								</button>
							)}
						</div>
						{/* <UncontrolledDropdown>
							<DropdownToggle color="link" size="sm" className="text-decoration-none">
								⋮
							</DropdownToggle>
							<DropdownMenu end>
								<DropdownItem header>View Documents</DropdownItem>
								{application.documents?.cv && (
									<DropdownItem onClick={() => openDocument(application.documents.cv)}>
										📄 CV/Resume
									</DropdownItem>
								)}
								{application.documents?.proposal && (
									<DropdownItem onClick={() => openDocument(application.documents.proposal)}>
										📝 Proposal
									</DropdownItem>
								)}
								{application.documents?.writingSample && (
									<DropdownItem onClick={() => openDocument(application.documents.writingSample)}>
										📖 Writing Sample
									</DropdownItem>
								)}
								{application.documents?.certificate && (
									<DropdownItem onClick={() => openDocument(application.documents.certificate)}>
										🎓 Certificate
									</DropdownItem>
								)}
								{application.documents?.recommendations && application.documents.recommendations.length > 0 && (
									<>
										<DropdownItem divider />
										<DropdownItem header>Recommendations</DropdownItem>
										{application.documents.recommendations.map((rec, idx) => (
											<DropdownItem key={idx} onClick={() => openDocument(rec.url)}>
												📬 Recommendation {idx + 1}
											</DropdownItem>
										))}
									</>
								)}
								<DropdownItem divider />
								<DropdownItem onClick={() => navigate(`/admin/applications/${application.id}`)}>
									👁️ View Full Application
								</DropdownItem>
							</DropdownMenu>
						</UncontrolledDropdown>
						 */}
					</td>
				);

			default:
				return <td>-</td>;
		}
	};

	return (
		<>
			<tr className="application-row">{visibleColumns.map((colKey) => renderCell(colKey))}</tr>

			{/* Score Details Modal */}
			<ScoreDetailsModal
				isOpen={scoreModal.isOpen}
				toggle={() => setScoreModal({ isOpen: false, round: null, reviewerId: null })}
				applicationId={application.id}
				round={scoreModal.round}
				reviewerId={scoreModal.reviewerId}
			/>
		</>
	);
};

export default ApplicationRow;
